import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, Layers, FilePlus2, Pencil } from "lucide-react"
import { useGetMyOrderRequestsQuery } from "shared/redux/endpoints/procurementApprovals"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { FilterBar, en } from "procurement/design"
import useProcurementFilters from "./hooks/useProcurementFilters"
import useProcurementListFilters from "./hooks/useProcurementListFilters"
import { ORDER_STATUS_LABEL, canOpenOrderEdit } from "./status"
import { normalizeOrderOrGroupItem } from "./constants"
import GroupOrderRow from "./components/GroupOrderRow"
import {
  Page,
  Card,
  Button,
  Checkbox,
  StatusBadge,
  Currency,
  DataTable,
  EmptyState,
} from "./ui"

const STATUS_OPTIONS = Object.entries(ORDER_STATUS_LABEL).map(
  ([value, label]) => ({ value, label })
)

const FILTER_DEFAULTS = {
  search: "",
  status: "",
  category: "",
  region: "",
  center: "",
  item: "",
  supplier: "",
  fromDate: "",
  toDate: "",
}

// ROLE_REQUESTER workspace: the user's own order requests.
// GET /private/procurement/order-requests/my-orders. Server-side filters:
// status, category, region, center, item, from/to date (the shared OR/PO/PR
// filter set, via buildProcurementListFilters) — only free-text search stays
// client-side, over the fetched page. (No longer in the sidenav — "My
// Orders" / "Order" cover the list views now — but kept mounted for existing
// links.)
const OrderRequestContainer = () => {
  const navigate = useNavigate()
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const { categoryOptions, regionOptions, centerOptions, itemOptions, itemsLoading, queryFilters } =
    useProcurementListFilters(filters)
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  const { data, isFetching } = useGetMyOrderRequestsQuery({
    ...queryFilters,
    status: filters.status || undefined,
    supplier: filters.supplier || undefined,
    size: 100,
  })

  // GET /order-requests/my-orders may return the same mixed order/group list
  // as GET /order-requests — normalize once, then split by type: plain orders
  // drive the main table (unchanged UX), groups render as expandable rows
  // above it. If this endpoint hasn't been enriched yet, every item just
  // normalizes to type "order" and `groups` stays empty.
  const items = useMemo(
    () => (data?.items || []).map(normalizeOrderOrGroupItem),
    [data]
  )
  const groups = useMemo(() => items.filter((it) => it.type === "group"), [items])

  const rows = useMemo(() => {
    const orderItems = items
      .filter((it) => it.type === "order")
      .map((it) => ({
        id: it.id,
        orCode: it.code,
        title: it.title,
        createdAt: it.createdAt,
        categoryId: it.categoryId,
        categoryName: it.categoryName,
        region: it.region,
        total: it.total,
        status: it.status,
        urgency: it.urgency,
        purchases: it.purchases,
        overBudget: it.overBudget,
      }))
    const q = filters.search.trim().toLowerCase()
    return q
      ? orderItems.filter((r) => `${r.orCode} ${r.title}`.toLowerCase().includes(q))
      : orderItems
  }, [items, filters.search])

  const openDetail = (row) => navigate(`/procurement/order-requests/${row.id}`)

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      if (allSelected) return new Set()
      return new Set(rows.map((r) => r.id))
    })

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds]
  )

  const createGroupOrder = () =>
    navigate("/procurement/order-requests/group/new", {
      state: { orders: selectedRows },
    })

  const columns = useMemo(
    () => [
      {
        key: "select",
        header: <Checkbox checked={allSelected} onChange={toggleSelectAll} />,
        className: "pc-col-select",
        render: (r) => (
          <Checkbox
            checked={selectedIds.has(r.id)}
            onChange={() => toggleSelected(r.id)}
          />
        ),
      },
      { key: "orCode", header: "OR Code", strong: true, sortable: true },
      { key: "title", header: "Title" },
      { key: "createdAt", header: "Created", sortable: true },
      { key: "categoryName", header: "Category" },
      { key: "region", header: "Region" },
      {
        key: "total",
        header: "Total",
        align: "right",
        sortable: true,
        value: (r) => r.total,
        render: (r) => <Currency value={r.total} />,
        className: "pc-col-total",
      },
      {
        key: "status",
        header: "Status",
        className: "pc-col-status",
        render: (r) => (
          <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            <StatusBadge status={r.status} />
            {r.purchases?.length > 0 && (
              <span className="pc-muted" style={{ fontSize: 12 }}>
                · {r.purchases.length} PO
              </span>
            )}
          </span>
        ),
      },
      {
        key: "urgency",
        header: "Urgency",
        render: (r) => (
          <span
            style={{
              fontSize: 12.5,
              fontWeight: (r.urgency || "").toLowerCase() === "urgent" ? 700 : 500,
              color:
                (r.urgency || "").toLowerCase() === "urgent"
                  ? "var(--pc-danger)"
                  : "var(--pc-muted)",
              textTransform: "capitalize",
            }}
          >
            {(r.urgency || "—").toLowerCase()}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <div
            style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}
          >
            {canOpenOrderEdit(r) && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Pencil aria-hidden />}
                onClick={() => navigate(`/procurement/order-requests/${r.id}/edit`)}
              >
                Edit
              </Button>
            )}
          </div>
        ),
      },
    ],
    [navigate, selectedIds, allSelected, rows]
  )

  const hasFilters = Boolean(
    filters.search ||
      filters.status ||
      filters.category ||
      filters.region ||
      filters.center ||
      filters.item ||
      filters.supplier ||
      filters.fromDate ||
      filters.toDate
  )

  return (
    <Page
      breadcrumb={["Procurement", "Order Request"]}
      title="Order Request"
      subtitle="Create purchase requests and track them through approval."
      actions={
        <>
          <Button
            variant="secondary"
            leftIcon={<Layers aria-hidden />}
            disabled={!selectedRows.length}
            onClick={createGroupOrder}
          >
            {selectedRows.length
              ? `Create Group Order (${selectedRows.length})`
              : "Create Group Order"}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus aria-hidden />}
            onClick={() => navigate("/procurement/order-requests/new")}
          >
            New Order Request
          </Button>
        </>
      }
    >
      <FilterBar
        values={filters}
        onChange={setFilters}
        onReset={resetFilters}
        fields={[
          { key: "search", label: en.filter.search, placeholder: en.filter.searchOrders },
          { key: "status", label: en.filter.status, type: "select", options: STATUS_OPTIONS, placeholder: en.filter.allStatuses },
          { key: "category", label: en.filter.category, type: "select", options: categoryOptions, placeholder: en.filter.allCategories },
          { key: "region", label: en.filter.region, type: "select", options: regionOptions },
          { key: "center", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
          { key: "item", label: en.filter.item, type: "multiselect", options: itemOptions, loading: itemsLoading, placeholder: en.filter.allItems, searchPlaceholder: en.filter.searchItem },
          { key: "supplier", label: en.filter.supplier, type: "select", options: supplierOptions, placeholder: en.filter.allSuppliers },
          { key: "fromDate", label: en.filter.createdFrom, type: "date" },
          { key: "toDate", label: en.filter.createdTo, type: "date" },
        ]}
      />
      {filters.fromDate && filters.toDate && filters.toDate < filters.fromDate && (
        <p className="pc-hint" style={{ margin: "-8px 0 12px", color: "var(--pc-danger)" }}>
          {en.filter.invalidDateRange}
        </p>
      )}

      {groups.length > 0 && (
        <Card
          flush
          icon={<Layers aria-hidden />}
          title="Group Orders"
          subtitle="Click a group to see the orders bundled inside it."
        >
          {groups.map((g) => (
            <GroupOrderRow
              key={`group-${g.id}`}
              group={g}
              onSelectOrder={(id) => navigate(`/procurement/order-requests/${id}`)}
            />
          ))}
        </Card>
      )}

      <Card flush>
        <DataTable
          columns={columns}
          rows={rows}
          isLoading={isFetching}
          onRowClick={openDetail}
          rowClassName={(r) =>
            selectedIds.has(r.id)
              ? "pc-row-selected"
              : r.overBudget
              ? "pc-row-over-budget"
              : r.purchases?.length > 0
              ? "pc-row-has-po"
              : undefined
          }
          initialSort={{ key: "createdAt", dir: "desc" }}
          empty={
            hasFilters ? (
              <EmptyState
                title="No requests match these filters"
                text="Try clearing the search or filters to see all of your requests."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={resetFilters}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<FilePlus2 aria-hidden />}
                title="No order requests yet"
                text="Raise your first request — pick a category, add vendor quotes, and submit it for approval."
                action={
                  <Button
                    variant="primary"
                    leftIcon={<Plus aria-hidden />}
                    onClick={() => navigate("/procurement/order-requests/new")}
                  >
                    New Order Request
                  </Button>
                }
              />
            )
          }
        />
      </Card>
    </Page>
  )
}

export default OrderRequestContainer
